import AngleLeft from "@icons/angle-left.svg?react";
import CrossSvg from "@icons/cross.svg?react";
import { msg } from "@lingui/core/macro";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import React from "react";
import { Navigate, useParams } from "react-router-dom";

import { Failed, NoData } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { PageTitle } from "@/common/components/page-title/page-title";
import {
  PriceItem,
  PriceList,
} from "@/common/components/price-list/price-list";
import {
  SearchField,
  useSearchFieldParams,
} from "@/common/components/search-field/search-field";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spinner } from "@/common/components/spinner/spinner";
import { WrapMeWith } from "@/common/components/wrap-me-with/wrap-me-with";
import { useCurrency } from "@/common/hooks/use-currency";
import { useLanguage } from "@/common/hooks/use-language";
import { useScrollbarToggle } from "@/common/hooks/use-scrollbar-toggle";
import { useToggle } from "@/common/hooks/use-toggle";
import { oneMinute } from "@/common/providers/query-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { getPricesView } from "@/common/utils/trading";
import { FeatureMode } from "@/domain/plan/feature-mode.service";
import { MarketplaceFlow } from "@/domain/plan/marketplace/marketplace.flow";
import { CropCategory, Currency } from "@/generated/digitalnisklady.cz/graphql";
import { TwoColumnLayout } from "@/routes/layout/layout";
import { LockedScreen } from "@/routes/shared/components/locked-screen/locked-screen";
import {
  PostalCodeDialog,
  usePostalCodeDialogParams,
} from "@/routes/shared/components/postal-code-dialog/postal-code-dialog";
import { PostalCodeInfoBox } from "@/routes/shared/components/postal-code-dialog/postal-code-info-box";
import { usePostalCodeObject } from "@/routes/shared/components/postal-code-dialog/use-postal-code-object";

import { CallToAction } from "./components/call-to-action";
import { Card } from "./components/card";
import { CategoryCard } from "./components/category-card";
import { SubcategoryCard } from "./components/subcategory-card";
import { marketplaceQuery } from "./marketplace.list.api";
import { getCategory, getCategoryOrder } from "./marketplace.utils";
import {
  categoryToSlug,
  getSubcategoriesForCategory,
  getSubcategoryOrder,
  getSubcategoryTranslation,
  hasSubcategories,
  slugToCategory,
  slugToSubcategory,
} from "./marketplace-subcategory.utils";

const path = "marketplace";

const Marketplace = ({ mode }: { mode: FeatureMode }) => {
  const flow = React.useRef(new MarketplaceFlow());
  flow.current.setStrategy(mode);
  const company = flow.current.getCompanyGetter()();
  const params = useMaterialParams();
  const postalCodeDialog = usePostalCodeDialogParams();
  useScrollbarToggle(postalCodeDialog.params?.enforceDialog);
  const [CurrencySwitch, currency] = useCurrency();
  const showPackages = useToggle();
  const postalCodeObject = usePostalCodeObject(
    postalCodeDialog.params?.postalCode,
  );
  const [search] = useSearchFieldParams();
  const locale = useLanguage();

  const currentCategory = params.categorySlug
    ? slugToCategory(params.categorySlug)
    : null;
  const currentSubcategory = params.subcategorySlug
    ? slugToSubcategory(params.subcategorySlug)
    : null;

  const marketplace = useQuery({
    refetchInterval: oneMinute,
    ...marketplaceQuery({
      companyId: company?.id,
      currency: currency as Currency,
      currentCategory,
      currentSubcategory,
      latitude: postalCodeObject.latitude!,
      locale,
      longitude: postalCodeObject.longitude!,
      search,
    }),
    enabled: !!postalCodeObject.latitude,
  });
  const { _ } = useLingui();
  const sidebar = useSidebar();

  let Jsx: React.ReactNode = null;

  if (!postalCodeObject.valid) {
    return <Navigate to="?" />;
  }

  if (showPackages.on) {
    return (
      <LockedScreen>
        <button className="mt-2 mr-2 self-start" onClick={showPackages.setOff}>
          <CrossSvg />
        </button>
      </LockedScreen>
    );
  }

  if (marketplace.status === "pending") {
    Jsx = (
      <div className="col-span-full flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (marketplace.status === "error") {
    Jsx = (
      <div className="col-span-full flex justify-center">
        <Failed error={marketplace.error} />
      </div>
    );
  }

  if (marketplace.status === "success") {
    if (marketplace.data.size === 0) {
      Jsx = (
        <div className="col-span-full flex justify-center">
          <NoData>
            <Trans>Nebyly nalezeny žádné záznamy</Trans>
          </NoData>
        </div>
      );
    } else if (!currentCategory) {
      // Show all categories sorted by custom order
      Jsx = [...marketplace.data.entries()]
        .map(([categoryKey]) => ({
          categoryKey,
          label: getCategory(categoryKey),
        }))
        .toSorted(
          (a, b) =>
            getCategoryOrder(a.categoryKey) - getCategoryOrder(b.categoryKey),
        )
        .map(({ categoryKey, label }) => {
          const isComingSoon = categoryKey === CropCategory.Seed;
          return (
            <CategoryCard
              badge={isComingSoon ? t`Připravujeme` : undefined}
              category={categoryKey}
              disabled={isComingSoon}
              key={categoryKey}
              label={label}
            />
          );
        });
    } else if (hasSubcategories(currentCategory) && !currentSubcategory) {
      // Show subcategories for this category
      const subcategories = getSubcategoriesForCategory(currentCategory);
      Jsx = subcategories
        .map((subcategory) => ({
          label: getSubcategoryTranslation(subcategory),
          subcategory,
        }))
        .toSorted((a, b) => {
          // Use custom order for Fertilizer, alphabetical for others
          if (currentCategory === CropCategory.Fertilizer) {
            return (
              getSubcategoryOrder(a.subcategory) -
              getSubcategoryOrder(b.subcategory)
            );
          }
          return a.label.localeCompare(b.label);
        })
        .map(({ label, subcategory }) => (
          <SubcategoryCard
            key={subcategory}
            label={label}
            subcategory={subcategory}
          />
        ));
    } else {
      // Show materials for the selected category/subcategory
      const materialsForCategory = marketplace.data.get(currentCategory);
      if (!materialsForCategory || materialsForCategory.length === 0) {
        Jsx = (
          <div className="col-span-full flex justify-center">
            <NoData>
              <Trans>Nebyly nalezeny žádné záznamy</Trans>
            </NoData>
          </div>
        );
      } else {
        const filteredMaterials = currentSubcategory
          ? materialsForCategory.filter(
              (m) => m.subcategory === currentSubcategory,
            )
          : materialsForCategory;

        const sortedMaterials = filteredMaterials
          .map((m) => ({ ...m, category: currentCategory }))
          .toSorted((material1, material2) =>
            material1.name.localeCompare(material2.name),
          );

        Jsx = sortedMaterials.map((material) => {
          return (
            <div key={material.name}>
              <Card {...material}>
                {getPricesView(material.prices).map((prices, i) => (
                  <PriceList
                    category={material.category}
                    className={
                      i === 0 && prices.length === 1
                        ? "[&>li]:col-start-4 [&>li]:border-r-0 [&>li]:border-l basis-full"
                        : "basis-full"
                    }
                    key={i}
                    prices={prices.map((p) =>
                      p?.type === "data"
                        ? {
                            currency,
                            id: p.id,
                            quarter: p.quarter,
                            value: p.price,
                          }
                        : null,
                    )}
                    renderPrice={(price) =>
                      price ? (
                        <WrapMeWith
                          when={flow.current.canInteract()}
                          wrapper={(children) => (
                            <SearchParamsLink
                              className="text-can-slate-blue-gray flex flex-col gap-y-2"
                              to={
                                material.crop.id +
                                "/quarter/" +
                                price.quarter.id
                              }
                            >
                              {children}
                            </SearchParamsLink>
                          )}
                        >
                          <PriceItem
                            category={material.category}
                            price={price}
                          />
                        </WrapMeWith>
                      ) : null
                    }
                  />
                ))}
              </Card>
            </div>
          );
        });
      }
    }
  }

  return (
    <TwoColumnLayout
      left={{
        content: (
          <div className="flex basis-full flex-col gap-6">
            <Metadata
              title={
                currentSubcategory && currentCategory
                  ? `${getCategory(currentCategory)}: ${getSubcategoryTranslation(currentSubcategory)}`
                  : currentCategory
                    ? getCategory(currentCategory)
                    : _(msg`Nákup materiálu`)
              }
            />
            {!currentCategory && (
              <PostalCodeInfoBox
                code={postalCodeObject.code}
                name={postalCodeObject.name}
                onPostalCodeChange={postalCodeDialog.enforceDialog}
              >
                <Trans>
                  Nyní zobrazujeme aktuální ceny zboží pro oblast dle poštovního
                  směrovacího čísla (PSČ)
                </Trans>
              </PostalCodeInfoBox>
            )}
            <SearchField
              placeholder={
                currentCategory
                  ? `${_(msg`Hledat`)} ${getCategory(currentCategory).toLowerCase()}`
                  : t`Hledat materiál`
              }
            />
            <div
              className={`grid grid-cols-1 gap-6  ${params.cropId || sidebar.state.screen ? "xl:grid-cols-2" : "md:grid-cols-2 2xl:grid-cols-3"}`}
            >
              {Jsx}
            </div>

            {postalCodeDialog.params?.enforceDialog ? (
              <PostalCodeDialog>
                <Trans>
                  Pro výpočet cen zboží vyplňte do pole níže PSČ umístění
                  skladu, kam chcete zboží navézt.
                </Trans>
              </PostalCodeDialog>
            ) : null}
            <p className="mt-auto mb-8 text-center text-sm">
              {marketplace.dataUpdatedAt > 0 ? (
                <>
                  <Trans>Aktualizováno</Trans>
                  {DateTime.fromMillis(
                    marketplace.dataUpdatedAt,
                  ).toLocaleString(DateTime.DATETIME_FULL)}
                </>
              ) : null}
            </p>
            {flow.current.showCallToAction() ? (
              <CallToAction onClick={showPackages.setOn} />
            ) : null}
          </div>
        ),
        header: (
          <>
            {currentCategory ? (
              <div className="flex items-center gap-2">
                <SearchParamsLink
                  className="text-can-primary-green"
                  to={
                    currentSubcategory
                      ? `/marketplace/${categoryToSlug(currentCategory)}`
                      : `/marketplace`
                  }
                >
                  <AngleLeft className="w-6 h-6" />
                </SearchParamsLink>
                <PageTitle>
                  {currentSubcategory
                    ? `${getCategory(currentCategory)}: ${getSubcategoryTranslation(currentSubcategory)}`
                    : getCategory(currentCategory)}
                </PageTitle>
              </div>
            ) : (
              <PageTitle>
                <Trans>Nákup materiálu</Trans>
              </PageTitle>
            )}
            {CurrencySwitch}
          </>
        ),
      }}
      right={!!params.cropId}
    />
  );
};

const useMaterialParams = () => {
  const params = useParams();

  return {
    categorySlug: params.categorySlug,
    cropId: params.cropId,
    subcategorySlug: params.subcategorySlug,
  };
};

export { Marketplace, path };
